/**
 * 系统大盘 (Admin) + 我的消耗 — 统计卡片、图表、效率指标
 */

import { authFetch } from './api.js';
import { setCurrentChartType, getCurrentChartType, setUsageChart, getUsageChart } from './state.js';
import { showToast, formatNumber, formatDate } from './utils.js';
import { registerViewLoader, jumpToView } from './navigation.js';
import { setDashboardContext } from './state.js';
import { getPeriodDateRange, formatRangeHint, applyDateInputs, readDateInputs } from './dashboard-context.js';
import { loadChartLibrary } from '../interaction/ui/chart-loader.js';

export function initStats() {
  registerViewLoader('usage', () => loadUsageStats());
  registerViewLoader('admin-stats', () => loadAdminStatsDashboard());

  // 我的消耗 — 统计周期
  document.querySelectorAll('#usage-period-toggle button').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('#usage-period-toggle button').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      loadUsageStats();
    });
  });

  // 我的消耗 — 图表切换按钮
  document.querySelectorAll('.chart-switch-btn').forEach(btn => {
    btn.addEventListener('click', () => switchChartType(btn.dataset.type));
  });

  // 系统大盘 — Period toggle
  document.querySelectorAll('#period-toggle button').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('#period-toggle button').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const periodToggle = document.getElementById('period-toggle');
      if (periodToggle) periodToggle.dataset.custom = '0';
      const range = getPeriodDateRange(btn.dataset.period || 'month');
      applyDateInputs('admin-stats-start', 'admin-stats-end', range);
      loadAdminStatsDashboard();
    });
  });

  // 系统大盘 — 自定义时间范围
  document.getElementById('admin-stats-apply-range-btn')?.addEventListener('click', () => {
    const range = readDateInputs('admin-stats-start', 'admin-stats-end', null);
    if (!range?.startDate || !range?.endDate) {
      showToast('请选择开始和结束日期', 'error');
      return;
    }
    if (range.startDate > range.endDate) {
      showToast('开始日期不能晚于结束日期', 'error');
      return;
    }
    const periodToggle = document.getElementById('period-toggle');
    if (periodToggle) periodToggle.dataset.custom = '1';
    document.querySelectorAll('#period-toggle button').forEach(b => b.classList.remove('active'));
    loadAdminStatsDashboard({
      period: 'custom',
      startDate: range.startDate,
      endDate: range.endDate,
    });
  });

  // 系统大盘 — Group filter
  const groupSelect = document.getElementById('dashboard-group-filter');
  if (groupSelect) {
    groupSelect.addEventListener('change', () => loadAdminStatsDashboard());
  }

  // 系统大盘 — Card click delegation
  document.getElementById('view-admin-stats')?.addEventListener('click', (e) => {
    const card = e.target.closest('.stat-card[data-drill]');
    if (!card) return;
    const view = card.dataset.drill;
    let params = {};
    try { params = JSON.parse(card.dataset.drillParams || '{}'); } catch { /* ignore */ }
    jumpToView(view, params);
  });
}

function createStatsElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

// ==================== 我的消耗 ====================
export async function loadUsageStats() {
  try {
    const period = document.querySelector('#usage-period-toggle button.active')?.dataset.period || 'month';
    const { startDate, endDate } = getPeriodDateRange(period);
    const hint = document.getElementById('usage-range-hint');
    if (hint) hint.textContent = '统计范围：' + formatRangeHint({ period, startDate, endDate });
    const chartTitle = document.getElementById('usage-chart-title');
    if (chartTitle) chartTitle.textContent = '每日消耗趋势（' + formatRangeHint({ period, startDate, endDate }) + '）';

    const params = new URLSearchParams({ startDate, endDate });

    const response = await authFetch(`/api/usage/summary?${params}`);
    if (!response.ok) throw new Error('Failed to load usage stats');

    const data = await response.json();
    renderUsageCards(data.summary);
    await renderUsageChart(data.dailyStats);
    renderUsageDetails(data.summary);
  } catch (error) {
    console.error('Error loading usage stats:', error);
    showToast('加载消耗统计失败', 'error');
  }
}

function renderUsageCards(stats) {
  const { totalInputTokens, totalOutputTokens } = resolveTextTokenTotals(stats);
  document.getElementById('stat-sellersprite').textContent = formatNumber(stats.marketDataCalls ?? stats.sellerspriteCalls ?? 0);
  document.getElementById('stat-gemini-input').textContent = formatNumber(totalInputTokens);
  document.getElementById('stat-gemini-output').textContent = formatNumber(totalOutputTokens);
  document.getElementById('stat-listings').textContent = formatNumber(stats.listingsCreated || 0);
  document.getElementById('stat-images').textContent = formatNumber(stats.imagesGenerated || 0);
}

async function renderUsageChart(dailyStats) {
  const canvas = document.getElementById('usage-chart');
  if (!canvas) return;
  const container = canvas.parentElement;

  const chart = getUsageChart();
  chart?.destroy?.();

  const existingMsg = container.querySelector('.admin-stats-chart-empty-msg');
  if (existingMsg) existingMsg.remove();

  if (!dailyStats || dailyStats.length === 0) {
    canvas.hidden = true;
    canvas.width = 0;
    canvas.height = 0;
    const msg = document.createElement('div');
    msg.className = 'admin-stats-chart-empty-msg';
    msg.setAttribute('role', 'status');
    msg.textContent = '暂无数据';
    container.appendChild(msg);
    setUsageChart(null);
    return;
  }

  const { Chart } = await loadChartLibrary();
  canvas.hidden = false;
  const ctx = canvas.getContext('2d');
  const currentChartType = getCurrentChartType();

  const labels = dailyStats.map(s => formatDate(s.date, 'short'));
  const data = currentChartType === 'sellersprite'
    ? dailyStats.map(s => s.sellerspriteCalls || 0)
    : dailyStats.map(s => {
      const inputTokens = s.textInputTokens ?? s.geminiInputTokens ?? 0;
      const outputTokens = s.textOutputTokens ?? s.geminiOutputTokens ?? 0;
      return Number(inputTokens || 0) + Number(outputTokens || 0);
    });

  const color = currentChartType === 'sellersprite' ? '#3b82f6' : '#10b981';
  const label = currentChartType === 'sellersprite' ? '市场数据调用次数' : '文本模型 Token 总量';

  const newChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label,
        data,
        backgroundColor: color + '99',
        borderColor: color,
        borderWidth: 1,
        maxBarThickness: 40
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 300,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (tipCtx) => `${label}: ${formatNumber(tipCtx.parsed.y)}`
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { color: '#71717a' },
          grid: { color: 'rgba(113,113,122,0.15)' }
        },
        x: {
          ticks: { color: '#71717a' },
          grid: { display: false }
        }
      }
    }
  });
  ctx.chart = newChart;
  setUsageChart(newChart);
}

function switchChartType(type) {
  setCurrentChartType(type);
  document.querySelectorAll('.chart-switch-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.type === type);
  });
  loadUsageStats();
}

function renderUsageDetails(stats) {
  const tbody = document.getElementById('usage-details-tbody');
  if (!tbody) return;
  const { totalInputTokens, totalOutputTokens } = resolveTextTokenTotals(stats);
  const rows = [
    { label: '市场数据 API 调用', value: formatNumber(stats.marketDataCalls ?? stats.sellerspriteCalls ?? 0) },
    { label: '文本模型输入 Token（Gemini+MiniMax）', value: formatNumber(totalInputTokens) },
    { label: '文本模型输出 Token（Gemini+MiniMax）', value: formatNumber(totalOutputTokens) },
    { label: 'MiniMax 文本模型输入 Token', value: formatNumber(stats.minimaxInputTokens || 0) },
    { label: 'MiniMax 文本模型输出 Token', value: formatNumber(stats.minimaxOutputTokens || 0) },
    { label: '创建的 Listing 数量', value: formatNumber(stats.listingsCreated || 0) },
    { label: 'LinkFox 算力消耗', value: formatNumber(stats.imagesGenerated || 0) },
  ];

  tbody.replaceChildren(...rows.map((rowData) => {
    const row = document.createElement('tr');
    row.append(
      createStatsElement('td', '', rowData.label),
      createStatsElement('td', 'usage-details-value-cell', rowData.value),
    );
    return row;
  }));
}

function resolveTextTokenTotals(stats = {}) {
  const textInputTokens = stats.textInputTokens;
  const textOutputTokens = stats.textOutputTokens;
  if (textInputTokens != null || textOutputTokens != null) {
    return {
      totalInputTokens: Number(textInputTokens || 0),
      totalOutputTokens: Number(textOutputTokens || 0),
    };
  }
  return {
    totalInputTokens: Number(stats.geminiInputTokens || 0) + Number(stats.minimaxInputTokens || 0),
    totalOutputTokens: Number(stats.geminiOutputTokens || 0) + Number(stats.minimaxOutputTokens || 0),
  };
}

// ==================== 系统大盘 (Admin) ====================
export async function loadAdminStatsDashboard(override = null) {
  try {
    const periodToggle = document.getElementById('period-toggle');
    const customMode = periodToggle?.dataset.custom === '1';
    const activePeriod = document.querySelector('#period-toggle button.active')?.dataset.period || 'month';
    const localRange = readDateInputs('admin-stats-start', 'admin-stats-end', getPeriodDateRange(activePeriod));
    const period = override?.period || (customMode ? 'custom' : activePeriod);
    const groupId = document.getElementById('dashboard-group-filter')?.value || 'all';
    const params = new URLSearchParams({ period });
    if (override?.startDate && override?.endDate) {
      params.set('startDate', override.startDate);
      params.set('endDate', override.endDate);
    } else if (period === 'custom' && localRange?.startDate && localRange?.endDate) {
      params.set('startDate', localRange.startDate);
      params.set('endDate', localRange.endDate);
    }
    if (groupId && groupId !== 'all') params.set('groupId', groupId);

    const statsRes = await authFetch(`/api/admin/stats?${params}`);

    if (!statsRes.ok) throw new Error('Failed to load admin stats');
    const data = await statsRes.json();

    setDashboardContext({
      period: data.period || period,
      startDate: data.startDate,
      endDate: data.endDate,
      groupId: groupId && groupId !== 'all' ? groupId : '',
    });
    applyDateInputs('admin-stats-start', 'admin-stats-end', {
      startDate: data.startDate,
      endDate: data.endDate,
    });
    renderAdminStatsGrid(data);
  } catch (error) {
    console.error('Error loading admin stats:', error);
    showToast('加载系统统计失败', 'error');
  }
}

function drillCtx(data, extra = {}) {
  return {
    period: data.period,
    startDate: data.startDate,
    endDate: data.endDate,
    groupId: data.groupId || '',
    ...extra,
  };
}

function createAdminStatsCard(cardData) {
  const card = createStatsElement('div', 'stat-card');
  if (cardData.drill) {
    card.dataset.drill = cardData.drill;
    card.dataset.drillParams = JSON.stringify(cardData.drillParams || {});
  }
  card.append(
    createStatsElement('div', 'stat-card-icon', cardData.icon),
    createStatsElement('div', 'stat-card-label', cardData.label),
    createStatsElement('div', 'stat-card-value', formatNumber(cardData.value)),
  );
  return card;
}

function replaceAdminStatsGrid(id, cards) {
  const grid = document.getElementById(id);
  if (!grid) return;
  grid.replaceChildren(...cards.map((card) => createAdminStatsCard(card)));
}

function renderAdminStatsGrid(data) {
  const pl = { today: '今日', week: '本周', month: '本月', custom: '自定义' }[data.period] || '本月';
  const eff = data.efficiency || {};
  const kpi = {
    todayActive: data.users?.todayActive || 0,
    monthListings: eff.totalListings || data.jobs?.completed || 0,
    imagesGenerated: data.images?.totalGenerated || 0,
    abnormalJobs: data.jobs?.failed || 0,
  };
  const dc = (x) => drillCtx(data, x);
  const effCards = [
    { icon: '🟢', label: '今日活跃用户', value: kpi.todayActive,
      drill: 'admin-drilldown', drillParams: dc({ drillType: 'activity' }) },
    { icon: '📄', label: '本月 Listing 数', value: kpi.monthListings,
      drill: 'admin-drilldown', drillParams: dc({ drillType: 'monthly-matrix' }) },
    { icon: '⚡', label: 'LinkFox 算力消耗', value: kpi.imagesGenerated,
      drill: 'admin-drilldown', drillParams: dc({ drillType: 'trend', metricType: 'images' }) },
    { icon: '❌', label: '异常任务数', value: kpi.abnormalJobs,
      drill: 'admin-jobs', drillParams: dc({ status: 'failed' }) },
  ];

  const usageCards = [
    { icon: '👥', label: '总用户数', value: data.users?.total || 0,
      drill: 'admin-users', drillParams: dc({}) },
    { icon: '👤', label: '有产出用户数', value: eff.totalUsers || 0,
      drill: 'admin-usage', drillParams: dc({ filter: 'productive' }) },
    { icon: '🔌', label: `${pl} API 调用`, value: data.apiCalls?.sellerspriteCalls || 0,
      drill: 'admin-drilldown', drillParams: dc({ drillType: 'trend', metricType: 'calls' }) },
    { icon: '📊', label: `${pl} Token 消耗`, value: data.tokens?.totalTokens || 0,
      drill: 'admin-drilldown', drillParams: dc({ drillType: 'trend', metricType: 'tokens' }) },
    { icon: '⚡', label: '平均 API 次数/Listing', value: eff.avgCallsPerListing || 0,
      drill: 'admin-drilldown', drillParams: dc({ drillType: 'listing-cost', sortField: 'calls' }) },
    { icon: '📝', label: '平均 Token/Listing', value: eff.avgTokensPerListing || 0,
      drill: 'admin-drilldown', drillParams: dc({ drillType: 'listing-cost', sortField: 'tokens' }) },
    { icon: '⚡', label: `${pl} LinkFox 算力`, value: data.images?.totalGenerated || 0,
      drill: 'admin-drilldown', drillParams: dc({ drillType: 'trend', metricType: 'images' }) },
  ];

  const jobsCards = [
    { icon: '⚙️', label: `${pl} 任务总数`, value: data.jobs?.total || 0,
      drill: 'admin-jobs', drillParams: dc({}) },
    { icon: '✅', label: `${pl} 已完成任务`, value: data.jobs?.completed || 0,
      drill: 'admin-jobs', drillParams: dc({ status: 'completed' }) },
    { icon: '❌', label: `${pl} 失败任务`, value: data.jobs?.failed || 0,
      drill: 'admin-jobs', drillParams: dc({ status: 'failed' }) },
    { icon: '🔄', label: `${pl} 运行中任务`, value: data.jobs?.running || 0,
      drill: 'admin-jobs', drillParams: dc({ status: 'running' }) },
  ];

  replaceAdminStatsGrid('admin-stats-efficiency-grid', effCards);
  replaceAdminStatsGrid('admin-stats-usage-grid', usageCards);
  replaceAdminStatsGrid('admin-stats-jobs-grid', jobsCards);
}
